import { getRefernceDataByType, REFERENCE_DATA_TYPES } from "@services/referenceDataApi";
import type { FromToLocation } from "@services/vessel-call/types";

// Cache for from-to-locations
let locationsCache: FromToLocation[] | null = null;
let locationsPromise: Promise<FromToLocation[]> | null = null;

// Fetches and caches from-to-locations for location name resolution
export const fetchTerminalLocations = async (
  service: string = "bff-vessel-call",
): Promise<FromToLocation[]> => {
  // Return cached data if available
  if (locationsCache) {
    return locationsCache;
  }

  // Return existing promise if fetch is in progress
  if (locationsPromise) {
    return locationsPromise;
  }

  // Create new fetch promise
  locationsPromise = getRefernceDataByType(REFERENCE_DATA_TYPES.FROM_TO_LOCATIONS, service)
    .then((data) => {
      const typedData = data as FromToLocation[];
      locationsCache = typedData;
      return typedData;
    })
    .catch((error) => {
      // Reset promise on error so it can be retried
      locationsPromise = null;
      throw error;
    }) as Promise<FromToLocation[]>;

  return locationsPromise;
};

// Resolves a location code to its location name
export const resolveTerminalName = (
  locationCode: string | null | undefined,
  locations: FromToLocation[],
): string => {
  if (!locationCode) return locationCode || "";

  const location = locations.find((loc) => loc.fromToLocationCode === locationCode);

  return location?.locationName || locationCode;
};

// Resolves location codes in a string (replaces codes with names)
export const resolveTerminalNamesInText = (
  text: string | null | undefined,
  locations: FromToLocation[],
): string => {
  if (!text) return text || "";

  // Try to find location codes in the text and replace them
  // This handles cases where the backend sends location codes
  let resolvedText = text;

  locations.forEach((location) => {
    // Replace location code with location name
    const codeRegex = new RegExp(`\\b${location.fromToLocationCode}\\b`, "gi");
    resolvedText = resolvedText.replace(codeRegex, location.locationName);
  });

  return resolvedText;
};
